import { useTranslation } from 'react-i18next';
import './CacheChoiceModal.css';

/**
 * Modal qui s'affiche quand des données utilisateur sont disponibles (compte ou cache)
 * Permet de choisir entre utiliser les données existantes ou en saisir de nouvelles
 * @param {Object} cachedData - Données utilisateur (du compte ou du cache)
 * @param {boolean} isFromAccount - True si les données viennent d'un compte connecté
 * @param {Function} onUseCachedData - Callback pour utiliser les données
 * @param {Function} onEnterNewData - Callback pour saisir de nouvelles données
 * @param {Function} onClose - Callback pour fermer le modal
 */
function CacheChoiceModal({ cachedData, isFromAccount = false, onUseCachedData, onEnterNewData, onClose }) {
  const { t } = useTranslation('cacheModal');
  const { t: tc } = useTranslation('common');

  // Formater les données pour l'affichage
  const formatGender = (gender) => {
    if (gender === 'male' || gender === 'homme') return tc('gender.male');
    if (gender === 'female' || gender === 'femme') return tc('gender.female');
    return gender;
  };

  // Déterminer l'image à afficher
  // Compte : avatar URL (plein corps, comportement existant)
  // Cache anonyme : avatar VTO en priorité, sinon selfie
  const imageUrl = isFromAccount ? cachedData.avatarUrl : null;
  const imageBase64 = !isFromAccount ? (cachedData.avatarBase64 || cachedData.selfieBase64) : null;
  // L'avatar VTO est plein corps → on zoome sur la tête ; le selfie est déjà un gros plan du visage
  const showFaceZoom = !isFromAccount && !!cachedData.avatarBase64;
  const hasImage = imageUrl || imageBase64;

  return (
    <div className="cache-modal-overlay" onClick={onClose}>
      <div className="cache-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cache-modal-close" onClick={onClose} aria-label={tc('buttons.close')}>
          ×
        </button>

        <div className="cache-modal-content">
          {/* Image de l'utilisateur */}
          {hasImage && (
            <div className="cache-modal-image">
              {showFaceZoom ? (
                <div
                  className="user-preview-avatar-face"
                  style={{ backgroundImage: `url(${imageBase64})` }}
                />
              ) : (
                <img
                  src={imageUrl || imageBase64}
                  alt={isFromAccount ? t('imageAlt.avatar') : t('imageAlt.photo')}
                  className="user-preview-image"
                />
              )}
            </div>
          )}

          <p className="cache-modal-title">
            <strong>{t('title')}</strong>
          </p>
          <p className="cache-modal-text">
            {isFromAccount
              ? t('description.fromAccount')
              : t('description.fromCache')}
          </p>

          {/* Résumé des données */}
          <div className="cached-data-summary">
            <div className="summary-grid">
              {cachedData.gender && (
                <div className="summary-item">
                  <span className="summary-label">{t('summary.gender')}</span>
                  <span className="summary-value">{formatGender(cachedData.gender)}</span>
                </div>
              )}
              {cachedData.height && (
                <div className="summary-item">
                  <span className="summary-label">{t('summary.height')}</span>
                  <span className="summary-value">{cachedData.height} {tc('units.cm')}</span>
                </div>
              )}
              {cachedData.weight && (
                <div className="summary-item">
                  <span className="summary-label">{t('summary.weight')}</span>
                  <span className="summary-value">{cachedData.weight} {tc('units.kg')}</span>
                </div>
              )}
              {cachedData.age && (
                <div className="summary-item">
                  <span className="summary-label">{t('summary.age')}</span>
                  <span className="summary-value">{cachedData.age} {tc('units.years', 'ans')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Boutons de choix */}
          <div className="cache-modal-buttons">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onUseCachedData}
            >
              {t('buttons.useCachedData')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onEnterNewData}
            >
              {t('buttons.enterNewData')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CacheChoiceModal;
